// ============================================================================
// DailysRecentContext — Phase 2.0.5
// ============================================================================
// Thin Provider for the recent-window dailys arrays used by the home dashboard
// and admin-side views. Full histories live elsewhere (LayerContext keeps
// allLayerDailys / allEggDailys; cattle + sheep full histories are fetched on
// demand inside their dedicated views). This context only holds the rolling
// recent-14-day windows that the home dashboard + per-species summary tiles
// consume.
//
//   broilerDailys      : recent poultry_dailys rows
//   pigDailys          : recent pig_dailys rows
//   layerDailysRecent  : recent layer_dailys rows
//   eggDailysRecent    : recent egg_dailys rows
//   cattleDailysRecent : recent cattle_dailys rows
//   sheepDailysRecent  : recent sheep_dailys rows
// ============================================================================
import {createContext, useContext, useState} from 'react';
import type {ReactNode} from 'react';

// Value type INFERRED from the builder below. Every one of these six is a raw
// dailys table row array — six different tables, all defined in SQL, none of
// them read by this module — so `unknown[]` is the honest element type and no
// row interface is invented here. The setters come free from useState.
function useDailysRecentValue() {
  const [broilerDailys, setBroilerDailys] = useState<unknown[]>([]);
  const [pigDailys, setPigDailys] = useState<unknown[]>([]);
  const [layerDailysRecent, setLayerDailysRecent] = useState<unknown[]>([]);
  const [eggDailysRecent, setEggDailysRecent] = useState<unknown[]>([]);
  const [cattleDailysRecent, setCattleDailysRecent] = useState<unknown[]>([]);
  const [sheepDailysRecent, setSheepDailysRecent] = useState<unknown[]>([]);

  return {
    broilerDailys,
    setBroilerDailys,
    pigDailys,
    setPigDailys,
    layerDailysRecent,
    setLayerDailysRecent,
    eggDailysRecent,
    setEggDailysRecent,
    cattleDailysRecent,
    setCattleDailysRecent,
    sheepDailysRecent,
    setSheepDailysRecent,
  };
}

type DailysRecentContextValue = ReturnType<typeof useDailysRecentValue>;

const DailysRecentContext = createContext<DailysRecentContextValue | null>(null);

export function DailysRecentProvider({children}: {children?: ReactNode}) {
  const value = useDailysRecentValue();
  return <DailysRecentContext.Provider value={value}>{children}</DailysRecentContext.Provider>;
}

// Asserts the provider, and that assertion is what gives every consumer a
// non-null type. Not a new failure mode: the hook used to return `null` outside
// its provider and every consumer destructures the result immediately, so the
// app ALREADY crashed at that exact line with an anonymous
// `TypeError: Cannot destructure property … of null`. Same failure, same point,
// named cause.
export function useDailysRecent(): DailysRecentContextValue {
  const value = useContext(DailysRecentContext);
  if (!value) throw new Error('useDailysRecent must be used within DailysRecentProvider');
  return value;
}
